import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { artworks } from '../mock';

const ArtworkDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const artwork = artworks.find(a => a.slug === slug);
  const [selectedImage, setSelectedImage] = useState(null);

  if (!artwork) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center bg-[#1a1a1a]">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Artwork not found</h2>
          <Button onClick={() => navigate('/')} className="bg-white text-black hover:bg-gray-200">
            Return Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-16 bg-[#1a1a1a]">
      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 border-b border-gray-800">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="group text-gray-400 hover:text-white hover:bg-gray-800"
        >
          <ArrowLeft className="mr-2 h-4 w-4 group-hover:-translate-x-1 transition-transform" />
          Back to Gallery
        </Button>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pb-20">
        {/* Title and Description */}
        <div className="mb-12">
          <h1 className="text-3xl md:text-4xl font-bold text-white tracking-tight uppercase mb-6">
            {artwork.title}
          </h1>
          
          <div className="border-l-2 border-white pl-6">
            <p className="text-lg text-gray-300 leading-relaxed">
              {artwork.description}
            </p>
          </div>
        </div>

        {/* Images Grid - Two Side by Side */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
          {artwork.images.map((image, index) => {
            const isReference = artwork.references && artwork.references.includes(image);
            
            return (
              <div
                key={index}
                className="relative aspect-square bg-[#2a2a2a] overflow-hidden border border-gray-600 cursor-pointer group"
                onClick={() => setSelectedImage(image)}
              >
                <img
                  src={image}
                  alt={`${artwork.title} - View ${index + 1}`}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                {/* References Label */}
                {isReference && (
                  <div className="absolute top-4 left-4 bg-white px-3 py-1 pointer-events-none z-10">
                    <p className="text-black text-xs font-bold uppercase tracking-wider">References</p>
                  </div>
                )}
                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <p className="text-white text-sm font-bold uppercase tracking-wider">View Full</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Specifications - At Bottom */}
        <div className="bg-[#2a2a2a] border border-gray-800 p-8 mt-12">
          <h3 className="text-xl font-bold text-white uppercase tracking-wider mb-6">Specifications</h3>
          
          <div className="mb-8">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-widest mb-2">Medium</p>
              <p className="text-base font-medium text-gray-200">{artwork.medium}</p>
            </div>
          </div>

          {/* Software Tools */}
          {artwork.software && artwork.software.length > 0 && (
            <div className="border-t border-gray-700 pt-6">
              <p className="text-xs text-gray-500 uppercase tracking-widest mb-4">Tools Used</p>
              <div className="flex flex-wrap gap-4 items-center">
                {artwork.software.map((tool, index) => (
                  <div key={index} className="flex items-center gap-2 bg-[#1a1a1a] px-3 py-2 border border-gray-700">
                    <span className="text-sm font-medium text-gray-300">{tool}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Full Image Modal */}
      <Dialog open={selectedImage !== null} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-7xl w-full bg-black/95 border-gray-800 p-0">
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-4 right-4 z-50 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="h-6 w-6 text-white" />
          </button>
          {selectedImage && (
            <div className="relative w-full h-[90vh] flex items-center justify-center p-4">
              <img
                src={selectedImage}
                alt={artwork.title}
                className="max-w-full max-h-full object-contain"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ArtworkDetail;
